// Pattern Analysis Module
// Handles all pattern detection calculations for pieces and lines

export const CANVAS_W = 800;
export const CANVAS_H = 560;

export function dist(a, b) {
  return Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);
}

export function getCentroid(points) {
  if (points.length === 0) return { x: CANVAS_W / 2, y: CANVAS_H / 2 };
  const sx = points.reduce((s, p) => s + p.x, 0);
  const sy = points.reduce((s, p) => s + p.y, 0);
  return { x: sx / points.length, y: sy / points.length };
}

// Extract points from lines for pattern analysis (sample every Nth point)
export function getLinePoints(lines, sampleRate = 5) {
  const points = [];
  for (const line of lines) {
    if (!line.points || line.points.length < 2) continue;
    for (let i = 0; i < line.points.length; i += sampleRate) {
      points.push({ x: line.points[i].x, y: line.points[i].y, fromLine: true });
    }
    // Always include endpoint
    const lastPt = line.points[line.points.length - 1];
    if (points.length === 0 || points[points.length - 1].x !== lastPt.x || points[points.length - 1].y !== lastPt.y) {
      points.push({ x: lastPt.x, y: lastPt.y, fromLine: true });
    }
  }
  return points;
}

// Combine pieces and line points for analysis
export function getAllPoints(pieces, lines = []) {
  const piecePoints = pieces.map(p => ({ x: p.x, y: p.y, fromLine: false }));
  const linePoints = getLinePoints(lines);
  return [...piecePoints, ...linePoints];
}

export function calcDensity(pieces, lines = []) {
  const allPoints = getAllPoints(pieces, lines);
  if (allPoints.length < 2) return 0;
  const c = getCentroid(allPoints);
  const avg = allPoints.reduce((s, p) => s + dist(p, c), 0) / allPoints.length;
  const maxR = Math.sqrt(CANVAS_W ** 2 + CANVAS_H ** 2) / 2;
  return Math.max(0, 1 - avg / maxR);
}

export function calcSymmetry(pieces, lines = []) {
  const allPoints = getAllPoints(pieces, lines);
  if (allPoints.length < 2) return 0;
  const cx = CANVAS_W / 2;
  let matches = 0;
  const thresh = 35;
  for (let i = 0; i < allPoints.length; i++) {
    const p = allPoints[i];
    const mx = 2 * cx - p.x;
    if (allPoints.some((q, j) => j !== i && Math.abs(q.x - mx) < thresh && Math.abs(q.y - p.y) < thresh)) matches++;
  }
  return matches / allPoints.length;
}

export function calcLinearity(pieces, lines = []) {
  const allPoints = getAllPoints(pieces, lines);
  if (allPoints.length < 3) return 0;
  const sample = allPoints.length > 15 ? allPoints.slice(-15) : allPoints;
  let col = 0;
  const thresh = 18;
  for (let i = 0; i < sample.length; i++) {
    for (let j = i + 1; j < sample.length; j++) {
      const d = dist(sample[i], sample[j]);
      if (d < 10) continue;
      for (let k = j + 1; k < sample.length; k++) {
        const area = Math.abs((sample[j].x - sample[i].x) * (sample[k].y - sample[i].y) -
          (sample[k].x - sample[i].x) * (sample[j].y - sample[i].y));
        if (area / d < thresh) col++;
      }
    }
  }
  const maxT = sample.length * (sample.length - 1) * (sample.length - 2) / 6;
  return maxT > 0 ? Math.min(1, col / (maxT * 0.08)) : 0;
}

export function calcRegularity(pieces, lines = []) {
  const allPoints = getAllPoints(pieces, lines);
  if (allPoints.length < 3) return 0;
  const nns = allPoints.map((p, i) => {
    let mn = Infinity;
    for (let j = 0; j < allPoints.length; j++) {
      if (i !== j) {
        const d = dist(p, allPoints[j]);
        if (d < mn) mn = d;
      }
    }
    return mn;
  });
  const mean = nns.reduce((a, b) => a + b, 0) / nns.length;
  const variance = nns.reduce((a, b) => a + (b - mean) ** 2, 0) / nns.length;
  const cv = mean > 0 ? Math.sqrt(variance) / mean : 1;
  return Math.max(0, 1 - cv);
}

export function patternConfidence(pieces, lines = []) {
  const allPoints = getAllPoints(pieces, lines);
  if (allPoints.length < 2) return 0;
  const d = calcDensity(pieces, lines);
  const s = calcSymmetry(pieces, lines);
  const l = calcLinearity(pieces, lines);
  const r = calcRegularity(pieces, lines);
  return d * 0.2 + s * 0.35 + l * 0.25 + r * 0.2;
}

export function historySimilarity(pieces, learning) {
  if (!learning || learning.total_attempts === 0 || pieces.length === 0) return 0;
  const cur = {};
  pieces.forEach(p => { cur[p.type] = (cur[p.type] || 0) + 1; });
  const prev = learning.preferred_shapes || {};
  const all = new Set([...Object.keys(cur), ...Object.keys(prev)]);
  let dot = 0, nA = 0, nB = 0;
  for (const t of all) { const a = cur[t] || 0; const b = prev[t] || 0; dot += a * b; nA += a * a; nB += b * b; }
  const shapeSim = (nA > 0 && nB > 0) ? dot / (Math.sqrt(nA) * Math.sqrt(nB)) : 0;

  const dna = (learning.fundamental_form_dna || []).filter(d => d.x != null);
  if (dna.length === 0) return shapeSim * 0.4;
  const cc = getCentroid(pieces);
  const dc = getCentroid(dna);
  const cd = dist(cc, dc);
  const spatialSim = Math.max(0, 1 - cd / 300);
  return shapeSim * 0.5 + spatialSim * 0.5;
}
